import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CEPTPreferencesForm.css';

function CEPTPreferencesForm({ onSubmit }) {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState({
    topic: '',
    questionType: 'reading',
    topicSource: 'custom' // 'custom' | 'topicOfTheDay'
  });
  const [topicOfTheDay, setTopicOfTheDay] = useState(null);
  const [loadingTopic, setLoadingTopic] = useState(true);
  const [showTopicPreview, setShowTopicPreview] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchTopicOfTheDay();
  }, []);

  const fetchTopicOfTheDay = async () => {
    try {
      setLoadingTopic(true);
      const response = await fetch('/api/topic-of-the-day');
      if (response.ok) {
        const data = await response.json();
        setTopicOfTheDay(data);
      } else {
        // Fallback topic if API fails
        setTopicOfTheDay({
          topic: 'Technology & Innovation',
          description: 'Explore the latest developments in technology and their impact on society',
          generatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Failed to fetch topic of the day:', error);
      // Fallback topic
      setTopicOfTheDay({
        topic: 'Environmental Sustainability',
        description: 'Learn about environmental challenges and sustainable solutions',
        generatedAt: new Date().toISOString()
      });
    } finally {
      setLoadingTopic(false);
    }
  };

  const handleTopicOfTheDaySelect = () => {
    if (topicOfTheDay) {
      setPreferences(prev => ({
        ...prev,
        topic: topicOfTheDay.topic,
        topicSource: 'topicOfTheDay'
      }));
      setShowSelectionModal(true);
    }
  };

  const handleInputChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value,
      topicSource: field === 'topic' ? 'custom' : prev.topicSource
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(preferences);
  };

  return (
    <div className="cept-preferences-form">
      <h2>📚 {t('cept.generatePaper', 'Cambridge CEPT Practice')}</h2>
      <p className="form-description">
        {t('cept.description', 'Generate personalized Cambridge English Placement Test (CEPT) practice materials tailored to your needs.')}
      </p>
      
      <form onSubmit={handleSubmit}>
        {/* Topic of the Day Section */}
        <div className="form-group topic-of-day-section">
          <label className="section-label">🎯 Topic of the Day</label>
          {loadingTopic ? (
            <div className="topic-of-the-day-loading" aria-live="polite">
              <div className="loading-spinner" aria-hidden="true"></div>
              <p>Loading today's topic...</p>
            </div>
          ) : topicOfTheDay ? (
            <div className="topic-of-day-card" onClick={() => setShowTopicPreview(true)} style={{ cursor: 'pointer' }}>
              <div className="topic-of-day-content">
                <div className="topic-preview-section">
                  <div className="topic-preview-badge">
                    <span className="preview-topic">{topicOfTheDay.topic}</span>
                    {topicOfTheDay.description && (
                      <p className="topic-description">{topicOfTheDay.description}</p>
                    )}
                  </div>
                </div>
                
                <button
                  type="button"
                  className="topic-of-day-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTopicOfTheDaySelect();
                  }}
                  aria-label={`Use today's topic: ${topicOfTheDay.topic}`}
                >
                  🚀 Use Today's Topic
                </button>
              </div>
            </div>
          ) : (
            <div className="topic-of-day-error" role="alert">
              <p>Unable to load today's topic. Please try again or enter a custom topic below.</p>
            </div>
          )}
          <p className="topic-hint">Fresh topic generated daily at 6:00 AM (UTC+8)</p>
        </div>

        {/* Separator */}
        <div className="topic-separator">
          <div className="separator-line"></div>
          <div className="separator-text">or</div>
          <div className="separator-line"></div>
        </div>

                  {/* Custom Topic Section */}
          <div className="form-group">
            <label htmlFor="topic">{t('labels.topic', 'Topic or Theme:')}</label>
            <div className="topic-input-container">
              <input
                type="text"
                id="topic"
                value={preferences.topic}
                onChange={(e) => handleInputChange('topic', e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && preferences.topic.trim()) {
                    setShowSelectionModal(true);
                  }
                }}
                placeholder={t('placeholders.topic', 'e.g., Technology, Environment, Education, Business...')}
                className="form-input"
              />
              {preferences.topic.trim() && (
                <button
                  type="button"
                  className="show-selection-btn"
                  onClick={() => setShowSelectionModal(true)}
                >
                  ✓
                </button>
              )}
            </div>
            <small>Enter a custom topic or use today's topic above</small>
          </div>

        <div className="form-group">
          <label>{t('labels.questionType', 'Question Type:')}</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="questionType"
                value="reading"
                checked={preferences.questionType === 'reading'}
                onChange={(e) => handleInputChange('questionType', e.target.value)}
              />
              <span className="radio-text">📖 {t('cept.readingComprehension', 'Reading Comprehension')}</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="questionType"
                value="grammar"
                checked={preferences.questionType === 'grammar'}
                onChange={(e) => handleInputChange('questionType', e.target.value)}
              />
              <span className="radio-text">📝 {t('cept.vocabularyGrammar', 'Vocabulary & Grammar')}</span>
            </label>
          </div>
        </div>

        <button type="submit" className="submit-btn">
          🚀 {t('cept.generateCEPT', 'Generate CEPT Practice')}
        </button>
      </form>

      {/* Topic Preview Popup */}
      {showTopicPreview && (
        <div className="topic-preview-overlay" onClick={() => setShowTopicPreview(false)}>
          <div className="topic-preview-popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="topic-preview-close" 
              onClick={() => setShowTopicPreview(false)}
              aria-label="Close topic preview"
            >
              ×
            </button>
            <div className="topic-preview-content">
              <h3>🎯 Today's Topic</h3>
              {topicOfTheDay && (
                <>
                  <div className="topic-preview-main">
                    <h4>{topicOfTheDay.topic}</h4>
                    <p>{topicOfTheDay.description}</p>
                  </div>
                  <div className="topic-preview-actions">
                    <button 
                      className="use-topic-btn"
                      onClick={() => {
                        handleTopicOfTheDaySelect();
                        setShowTopicPreview(false);
                      }}
                    >
                      🚀 Use This Topic
                    </button>
                    <button 
                      className="generate-btn modal-generate"
                      onClick={() => {
                        handleTopicOfTheDaySelect();
                        setShowTopicPreview(false);
                        // Trigger form submission
                        const form = document.querySelector('form');
                        if (form) {
                          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        }
                      }}
                    >
                      Generate CEPT Practice
                    </button>
                    <button 
                      className="close-preview-btn"
                      onClick={() => setShowTopicPreview(false)}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selection Confirmation Modal */}
      {showSelectionModal && (
        <div className="selection-modal-overlay" onClick={() => setShowSelectionModal(false)}>
          <div className="selection-modal" onClick={e => e.stopPropagation()}>
            <button type="button" className="selection-modal-close" onClick={() => setShowSelectionModal(false)} aria-label="Close selection modal">×</button>
            <div className="selection-modal-content">
              <h3>Selected Topic</h3>
              <div className="selected-topic-display modal-compact">
                <div className="selected-topic">
                  <span className="topic-text">{preferences.topic}</span>
                  <button
                    type="button"
                    className="clear-topic-btn"
                    onClick={() => {
                      setPreferences(prev => ({ ...prev, topic: '', topicSource: 'custom' }));
                      setShowSelectionModal(false);
                    }}
                    aria-label="Clear selected topic"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <button 
                type="submit" 
                className="generate-btn modal-generate"
                onClick={() => setShowSelectionModal(false)}
              >
                Generate CEPT Practice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="error-modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="error-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="error-modal-close" 
              onClick={() => setShowErrorModal(false)}
              aria-label="Close error modal"
            >
              ×
            </button>
            <div className="error-modal-content">
              <div className="error-icon">⚠️</div>
                             <h3>Failed to Generate Study Guide</h3>
               <p>{errorMessage}</p>
               <div className="error-modal-actions">
                 <button
                   className="try-again-btn"
                   onClick={() => {
                     setShowErrorModal(false);
                     // Trigger form submission again
                     const form = document.querySelector('form');
                     if (form) {
                       form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                     }
                   }}
                 >
                   🔄 Try Again
                 </button>
                 <button
                   className="discord-btn"
                   onClick={() => {
                     // Open Discord support server
                     window.open('https://discord.gg/papertips', '_blank');
                   }}
                 >
                   💬 Discord Support
                 </button>
               </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CEPTPreferencesForm; 